import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from '../../store/store';
import {
  setFullPageLoader,
  setIsPremium,
  setPremiumDetails,
  setUser,
} from '../../store/reducers';
import { selectIsDisclaimerVisible } from '../../store/selectors';
import {
  cookiesUtils,
  extensionUtils,
  firebaseUtils,
  logger,
  mixpanelUtils,
  sendAppsFlyerEvent,
  setAppsFlyerCustomerUserId,
} from '../../utils';
import { ELoginProvider, IUser } from '../../models';
import { isUserAnonymous } from '../../utils/helpers/user';
import { useSync } from '../data/useSync';
import { useAcceptTermsOnAuth } from '@/features/dataConsent/useAcceptTermsOnAuth.hook';
import { useFeatureFlag } from '../config/useFeatureFlag.hook';
import { useMixpanel } from '../integrations/useMixpanel.hook';
import { apiUser } from '../../utils/api/apiUser';
import { UserCredential } from 'firebase/auth';
import { ANALYTICS } from '../../constants/analytics/analytics';
import { EAnalyticsProvider, useAnalytics } from '../analytics/useAnalytics';
import { useExtension } from '../integrations/useExtension.hook';
import {
  LOGGER_ACTION,
  LOGGER_FEATURE,
} from '../../constants/logger/logger-tags';
import { COOKIE_KEYS } from '@/constants/storage/cookies';
import { APPSFLYER_EVENT_NAME } from '@/constants/integrations/appsflyer';

// todo -Adding an error message to the user anywhere here in the file instead of throwing an error
/**
 * Hook for user authentication and management using Redux
 */
export const useUser = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const syncHook = useSync();
  const { acceptTermsOnAuth } = useAcceptTermsOnAuth();
  const { updateAttributes } = useFeatureFlag();
  const mixpanelHook = useMixpanel();
  const analyticsHook = useAnalytics();
  // Get user state from Redux
  const user = useSelector((state: RootState) => state.user);
  const extensionHook = useExtension();

  /**
   * Initialize Firebase and authenticate anonymously if needed
   * Errors are logged at source (firebase.ts) and handled by AppInit
   */
  const init = async (): Promise<IUser> => {
    const fbUser = await firebaseUtils.init();
    const tmpUser = await firebaseUtils.fbUserToUser(fbUser);
    dispatch(setUser(tmpUser));
    return tmpUser;
  };

  const reloadPremiumDetails = async () => {
    const premiumDetailsResponse = await apiUser.getPremiumDetails();
    const isPremium = premiumDetailsResponse?.isPremium ?? false;

    dispatch(setIsPremium(isPremium));
    // extension only: the web app already has the status from the server
    extensionUtils.syncPremiumStatus(isPremium);

    // Update Mixpanel user tier - this is the source of truth for premium status on purchase/reload
    mixpanelUtils.setUserTier(isPremium);

    if (isPremium) {
      logger.addBreadcrumb(
        'Premium details reloaded',
        'user-management',
        'info',
        { isPremium }
      );
    }

    if (premiumDetailsResponse?.premiumDetails) {
      dispatch(setPremiumDetails(premiumDetailsResponse.premiumDetails));
    }

    return premiumDetailsResponse;
  };
  /**
   * Check if an email already exists in the firebase
   */
  const checkIfEmailExists = async (email: string): Promise<boolean> => {
    return await apiUser.checkIfEmailExists(email);
  };

  /**
   * Send GTM identifiers from cookies so the backend can attribute the user
   */
  const sendGtmData = async () => {
    const gtmIdentifiers = cookiesUtils.getCookie(COOKIE_KEYS.GA_IDENTIFIERS);
    if (!gtmIdentifiers) {
      logger.debug('no gtm identifiers in cookies, skipping');
      return;
    }
    const gtmServerPreview = cookiesUtils.getCookie(
      COOKIE_KEYS.GA_SERVER_PREVIEW
    );
    await apiUser.setGtmData(gtmIdentifiers, gtmServerPreview ?? undefined);
  };

  /**
   * handle what should happen after user login / register
   */
  const handlePostLogin = async (
    userCredentials: UserCredential,
    source: string,
    loginType: ELoginProvider
  ) => {
    // captured before any await so it reflects the triggering screen, not a later one
    const sawDisclaimer = selectIsDisclaimerVisible(store.getState());
    const user = await firebaseUtils.fbUserToUser(
      firebaseUtils.getCurrentUser()
    );
    dispatch(setUser(user));

    // Set user context for Sentry after successful login
    logger.setUser({
      id: user.uid,
      email: user.email || undefined,
      username: user.displayName || undefined,
    });

    setAppsFlyerCustomerUserId(user.uid);
    logger.addBreadcrumb('User logged in', 'authentication', 'info', {
      source,
    });

    await syncHook.syncFromRemote(true);
    await updateAttributes({ loggedIn: true });
    mixpanelHook.identify(user.uid, user.email || undefined);
    if (sawDisclaimer) {
      await acceptTermsOnAuth(source);
    }
    extensionHook.sendAccessTokenToExtension();
    await sendGtmData();
    const info = firebaseUtils.getAdditionalUserInfo(userCredentials);
    sendAppsFlyerEvent(
      info?.isNewUser
        ? APPSFLYER_EVENT_NAME.USER_REGISTER
        : APPSFLYER_EVENT_NAME.USER_LOGIN
    );
    if (info?.isNewUser) {
      analyticsHook.fireAnalyticsEvent(
        ANALYTICS.EVENTS.REGISTRATION,
        {
          [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.REGISTRATION,
          [ANALYTICS.PROPERTIES.SOURCE]: source,
          [ANALYTICS.PROPERTIES.EMAIL]: userCredentials.user.email,
          [ANALYTICS.PROPERTIES.LOGIN_TYPE]: loginType,
        },
        [EAnalyticsProvider.All]
      );
    } else {
      analyticsHook.fireAnalyticsEvent(
        ANALYTICS.EVENTS.LOGIN,
        {
          [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.LOGIN,
          [ANALYTICS.PROPERTIES.SOURCE]: source,
          [ANALYTICS.PROPERTIES.EMAIL]: userCredentials.user.email,
          [ANALYTICS.PROPERTIES.LOGIN_TYPE]: loginType,
        },
        [EAnalyticsProvider.All]
      );
    }
  };

  /**
   * Logout current user
   * @param source - The source of the logout action (e.g. ACCOUNT_DROPDOWN, CHECKOUT)
   */
  const logout = useCallback(
    async (source: string) => {
      dispatch(setFullPageLoader(true));
      try {
        mixpanelHook.track(ANALYTICS.EVENTS.LOGOUT, {
          [ANALYTICS.PROFILE_PROPERTIES.IS_LOGGED_IN]: false,
          [ANALYTICS.PROPERTIES.SOURCE]: source,
        });

        await extensionHook.logoutExtension();
        await firebaseUtils.logoutUser();
        await firebaseUtils.initFirebaseAnonymously(true);
        dispatch(
          setUser(
            await firebaseUtils.fbUserToUser(firebaseUtils.getCurrentUser())
          )
        );

        // Clear user context from Sentry after logout
        logger.setUser(null);
        logger.addBreadcrumb('User logged out', 'authentication', 'info', {
          source,
        });

        // Update Mixpanel profile on logout (no reset to keep device_id)
        mixpanelHook.onLogout();

        await syncHook.clearConsentAndInitialize();
        await extensionHook.sendAccessTokenToExtension();
        dispatch(setFullPageLoader(false));
        return true;
      } catch (error) {
        logger.error('Failed to logout user', error, {
          tags: {
            feature: LOGGER_FEATURE.AUTHENTICATION,
            action: LOGGER_ACTION.LOGOUT,
          },
        });
        dispatch(setFullPageLoader(false));
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Login with email and password
   */
  const signInWithEmailPassword = useCallback(
    async (email: string, password: string, source: string) => {
      try {
        dispatch(setFullPageLoader(true));
        const userCredentials = await firebaseUtils.signInWithEmailAndPassword(
          email,
          password
        );
        await handlePostLogin(userCredentials, source, ELoginProvider.email);
        dispatch(setFullPageLoader(false));
      } catch (error) {
        logger.error('Failed to sign in with email and password', error, {
          tags: {
            feature: LOGGER_FEATURE.AUTHENTICATION,
            action: LOGGER_ACTION.EMAIL_LOGIN,
          },
        });
        dispatch(setFullPageLoader(false));
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Register with email and password
   */
  const register = useCallback(
    async (
      email: string,
      password: string,
      displayName: string,
      source: string
    ) => {
      try {
        dispatch(setFullPageLoader(true));
        const userCredentials =
          await firebaseUtils.registerWithEmailAndPassword(email, password);
        await firebaseUtils.updateProfile(displayName);
        await handlePostLogin(userCredentials, source, ELoginProvider.email);
        dispatch(setFullPageLoader(false));
      } catch (error) {
        logger.error('Failed to register user', error, {
          tags: {
            feature: LOGGER_FEATURE.AUTHENTICATION,
            action: LOGGER_ACTION.REGISTER,
          },
          extra: { email },
        });
        dispatch(setFullPageLoader(false));
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Login with Provider
   */
  const loginWithProvider = useCallback(
    async (provider: ELoginProvider, source: string) => {
      const userCredentials = await firebaseUtils.loginWithProvider(provider);
      await handlePostLogin(userCredentials, source, provider);
    },
    [dispatch]
  );

  const loginWithGoogleOneTap = useCallback(
    async (credential: string) => {
      try {
        const userCredentials =
          await firebaseUtils.loginWithGoogleOneTap(credential);
        await handlePostLogin(
          userCredentials,
          ANALYTICS.VALUES.ONE_TAP_LOGIN,
          ELoginProvider.google
        );
      } catch (error) {
        logger.error('Failed to login with Google One Tap', error, {
          tags: {
            feature: LOGGER_FEATURE.AUTHENTICATION,
            action: LOGGER_ACTION.GOOGLE_ONE_TAP,
          },
        });
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Update user profile
   */
  const updateProfile = useCallback(
    async (displayName: string) => {
      try {
        await firebaseUtils.updateProfile(displayName);
      } catch (error) {
        logger.error('Failed to update user profile', error, {
          tags: {
            feature: LOGGER_FEATURE.USER_PROFILE,
            action: LOGGER_ACTION.UPDATE,
          },
        });
        throw error;
      }
    },
    [dispatch]
  );
  /**
   * Get the current user's authentication token
   */
  const getUserToken = useCallback(async () => {
    return await firebaseUtils.getUserToken();
  }, []);

  const sendResetPasswordEmail = useCallback(async (email: string) => {
    await firebaseUtils.sendPasswordResetEmail(email);
  }, []);

  const isLogin = () => !isUserAnonymous(user);
  const isPremium = () => user.isPremium;

  const getDisplayNameFromEmail = (email: string): string => {
    return email.split('@')[0];
  };

  return {
    user,
    handlePostLogin,
    reloadPremiumDetails,
    init,
    signInWithEmailPassword,
    register,
    loginWithProvider,
    logout,
    getUserToken,
    isLogin,
    isPremium,
    updateProfile,
    sendResetPasswordEmail,
    loginWithGoogleOneTap,
    checkIfEmailExists,
    getDisplayNameFromEmail,
  };
};
